import { useState, useEffect, useCallback } from 'react';
import { ChapletType } from '../types';
import {
  loadChapletFavorites,
  toggleChapletFavorite,
  reorderChapletFavorites,
  getFavoriteChapletsForDisplay
} from '../utils/chapletFavorites';

export interface UseChapletFavoritesReturn {
  favorites: ChapletType[];
  displayFavorites: ChapletType[];
  isFavorite: (chapletType: ChapletType) => boolean;
  toggleFavorite: (chapletType: ChapletType) => void;
  reorderFavorites: (reorderedFavorites: ChapletType[]) => void;
  refreshFavorites: () => void;
}

/**
 * Custom hook for managing chaplet favorites
 */
export const useChapletFavorites = (): UseChapletFavoritesReturn => {
  const [favorites, setFavorites] = useState<ChapletType[]>([]);
  const [displayFavorites, setDisplayFavorites] = useState<ChapletType[]>([]);

  // Refresh function to load from storage
  const refreshFromStorage = useCallback(() => {
    const loadedFavorites = loadChapletFavorites();
    setFavorites(loadedFavorites);
    setDisplayFavorites(getFavoriteChapletsForDisplay());
  }, []);

  // Load favorites on mount
  useEffect(() => {
    refreshFromStorage();
  }, [refreshFromStorage]);

  // Listen for storage changes to sync across hook instances
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'ora-chaplet-favorites') {
        refreshFromStorage();
      }
    };

    const handleCustomEvent = () => {
      refreshFromStorage();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('chaplet-favorites-changed', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('chaplet-favorites-changed', handleCustomEvent);
    };
  }, [refreshFromStorage]);

  /**
   * Check if a chaplet is favorited
   */
  const isFavorite = (chapletType: ChapletType): boolean => {
    return favorites.includes(chapletType);
  };

  /**
   * Toggle favorite status of a chaplet
   */
  const toggleFavorite = (chapletType: ChapletType): void => {
    const result = toggleChapletFavorite(chapletType);
    setFavorites(result.favorites);
    setDisplayFavorites(getFavoriteChapletsForDisplay());
  };

  /**
   * Reorder favorites
   */
  const reorderFavorites = (reorderedFavorites: ChapletType[]): void => {
    reorderChapletFavorites(reorderedFavorites);
    setFavorites(reorderedFavorites);
    setDisplayFavorites(getFavoriteChapletsForDisplay());
  };

  /**
   * Refresh favorites from storage (useful after external changes)
   */
  const refreshFavorites = (): void => {
    refreshFromStorage();
  };

  return {
    favorites,
    displayFavorites,
    isFavorite,
    toggleFavorite,
    reorderFavorites,
    refreshFavorites
  };
};